import React, { useState } from 'react';
import { 
  DocumentTextIcon, 
  ChatBubbleLeftEllipsisIcon, 
  ArrowPathIcon,
  UserGroupIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

const SuperAdminSettings = () => {
  const navigate = useNavigate();
  const [blockedKeywords, setBlockedKeywords] = useState('');
  const [currentBlockedKeywords, setCurrentBlockedKeywords] = useState(['spam', 'scam', 'free money', 'lottery']);
  const [selectedSection, setSelectedSection] = useState('');

  const handleAddBlockedKeywords = () => {
    if (!blockedKeywords.trim()) return;

    // Split the input by commas and clean up each keyword
    const newKeywords = blockedKeywords
      .split(',')
      .map(keyword => keyword.trim())
      .filter(keyword => keyword.length > 0);

    // Add new keywords while avoiding duplicates
    setCurrentBlockedKeywords(prev => {
      const uniqueKeywords = [...prev];
      newKeywords.forEach(keyword => {
        if (!uniqueKeywords.includes(keyword)) {
          uniqueKeywords.push(keyword);
        }
      });
      return uniqueKeywords;
    });

    // Clear the input field
    setBlockedKeywords('');
  };

  return (
    <div className="p-6 space-y-8">
      <h1 className="text-2xl font-medium mb-6">Super Admin Settings</h1>

      {/* Section 1: Section and Page Management */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <DocumentTextIcon className="h-6 w-6 text-[#FF5722] mr-2" />
            <h2 className="text-lg font-medium">Section and Page Management</h2>
          </div>
          <button 
            onClick={() => navigate('/superadmin/settings/add-section')}
            className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#FF5722]/90 transition-colors"
          >
            Add New Section
          </button>
        </div>

        <div className="space-y-4">
          {['Dashboard', 'SMS', 'Contacts', 'Templates', 'Reports'].map((section) => (
            <div key={section} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <h3 className="font-medium">{section}</h3>
                <p className="text-sm text-gray-600">Status: Published</p>
              </div>
              <div className="flex space-x-2">
                <button className="text-gray-600 hover:text-[#FF5722] px-3 py-1 rounded-md border">
                  Edit
                </button>
                <button className="text-red-600 hover:text-red-700 px-3 py-1 rounded-md border">
                  Unpublish
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Section 2: Message Management */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
          <ChatBubbleLeftEllipsisIcon className="h-6 w-6 text-[#FF5722] mr-2" />
          <h2 className="text-lg font-medium">Message Management</h2>
          </div>
          <button 
            onClick={handleAddBlockedKeywords}
            className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#FF5722]/90 transition-colors"
          >
            Add Blocked Keywords
          </button>
        </div>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Blocked Keywords
            </label>
            <textarea
              value={blockedKeywords}
              onChange={(e) => setBlockedKeywords(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
              rows={4}
              placeholder="Enter keywords to block (comma-separated)"
            />
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Currently Blocked Keywords</h3>
            <div className="flex flex-wrap gap-2">
              {currentBlockedKeywords.map((keyword, index) => (
                <span key={index} className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm">
                  {keyword}
                </span>
              ))}
            </div>
          </div>

          {/* New Blocked Messages History Section */}
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium">Blocked Messages History</h3>
              <div className="flex space-x-2">
                <button 
                  onClick={() => {/* Handle PDF export */}}
                  className="flex items-center px-4 py-2 text-sm bg-[#FF5722] text-white rounded-md hover:bg-[#FF5722]/90 transition-colors"
                >
                  Export as PDF
                </button>
                <button 
                  onClick={() => {/* Handle Excel export */}}
                  className="flex items-center px-4 py-2 text-sm bg-[#FF5722] text-white rounded-md hover:bg-[#FF5722]/90 transition-colors"
                >
                  Export as Excel
                </button>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date & Time</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Message Content</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Blocked Keywords</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Platform</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {[
                    {
                      datetime: '2024-03-20 14:30:00',
                      user: 'john.doe@example.com',
                      content: 'Win free money in our lottery! Contact now!',
                      keywords: ['free money', 'lottery'],
                      platform: 'Web Dashboard',
                      status: 'Blocked'
                    },
                    {
                      datetime: '2024-03-19 09:15:00',
                      user: 'jane.smith@example.com',
                      content: 'This is not a scam! Guaranteed returns!',
                      keywords: ['scam'],
                      platform: 'API',
                      status: 'Blocked'
                    },
                    {
                      datetime: '2024-03-18 16:45:00',
                      user: 'mark.wilson@example.com',
                      content: 'Special spam offer just for you!',
                      keywords: ['spam'],
                      platform: 'Mobile App',
                      status: 'Blocked'
                    }
                  ].map((entry, index) => (
                    <tr key={index} className="bg-white">
                      <td className="px-4 py-4 text-sm text-gray-900 whitespace-nowrap">
                        {entry.datetime}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {entry.user}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        <div className="max-w-md overflow-hidden text-ellipsis">
                          {entry.content}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm">
                        <div className="flex flex-wrap gap-1">
                          {entry.keywords.map((keyword, kidx) => (
                            <span key={kidx} className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">
                              {keyword}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs">
                          {entry.platform}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm">
                        <button className="text-[#FF5722] hover:text-[#FF5722]/90 text-sm font-medium">
                          Review
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex justify-between items-center">
              <div className="text-sm text-gray-600">
                Showing 3 of 150 blocked messages
              </div>
              <div className="flex space-x-2">
                <button className="px-3 py-1 border rounded-md text-sm text-gray-600 hover:bg-gray-50">
                  Previous
                </button>
                <button className="px-3 py-1 border rounded-md text-sm text-gray-600 hover:bg-gray-50">
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section 3: System Updates */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center mb-6">
          <ArrowPathIcon className="h-6 w-6 text-[#FF5722] mr-2" />
          <h2 className="text-lg font-medium">System Updates</h2>
        </div>

        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-medium text-blue-800 mb-2">Recommended Updates</h3>
            <p className="text-sm text-blue-600">Security patch v2.1.0 available</p>
            <button className="mt-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors text-sm">
              Install Update
            </button>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium">Update History</h3>
              <div className="flex space-x-2">
                <button 
                  onClick={() => {/* Handle PDF export */}}
                  className="flex items-center px-4 py-2 text-sm bg-[#FF5722] text-white rounded-md hover:bg-[#FF5722]/90 transition-colors"
                >
                  Export as PDF
                </button>
                <button 
                  onClick={() => {/* Handle Excel export */}}
                  className="flex items-center px-4 py-2 text-sm bg-[#FF5722] text-white rounded-md hover:bg-[#FF5722]/90 transition-colors"
                >
                  Export as Excel
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Update History Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Update Date and Time</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Update Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Update Success</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Update Summary</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {[
                    { 
                      name: 'Security Patch Update',
                      datetime: '2024-03-15 09:30:00',
                      user: 'John Admin',
                      type: 'Security',
                      success: true,
                      summary: 'Successfully patched vulnerability CVE-2024-001. All systems operating normally.'
                    },
                    { 
                      name: 'New Features Release',
                      datetime: '2024-03-01 14:15:00',
                      user: 'Sarah Manager',
                      type: 'Feature',
                      success: true,
                      summary: 'Added message scheduling and advanced filtering capabilities. User feedback positive.'
                    },
                    { 
                      name: 'Performance Optimization',
                      datetime: '2024-02-15 11:45:00',
                      user: 'Mike Developer',
                      type: 'Bug Fix',
                      success: false,
                      summary: 'Database optimization partially completed. Rollback initiated due to performance issues.'
                    }
                  ].map((update, index) => (
                    <tr key={index} className="bg-white">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {update.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {update.datetime}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {update.user}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    update.type === 'Security' ? 'bg-red-100 text-red-800' :
                    update.type === 'Feature' ? 'bg-green-100 text-green-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {update.type}
                  </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          update.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {update.success ? 'Successful' : 'Failed'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-md">
                        <div className="line-clamp-2">
                          {update.summary}
                </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
          </div>
        </div>
      </div>

      {/* Section 4: User Management */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center mb-6">
          <UserGroupIcon className="h-6 w-6 text-[#FF5722] mr-2" />
          <h2 className="text-lg font-medium">User Management</h2>
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium mb-2">Total Users</h3>
              <div className="text-2xl font-bold text-[#FF5722]">1,234</div>
              <p className="text-sm text-gray-600">Active accounts</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium mb-2">New Users</h3>
              <div className="text-2xl font-bold text-[#FF5722]">56</div>
              <p className="text-sm text-gray-600">This month</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium mb-2">Pending Approvals</h3>
              <div className="text-2xl font-bold text-[#FF5722]">12</div>
              <p className="text-sm text-gray-600">Awaiting review</p>
            </div>
          </div>

          {/* New Smart Filters Section */}
          <div className="bg-gray-50 p-4 rounded-lg space-y-4">
            <h3 className="font-medium text-gray-700">Smart Filters</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Company Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Company
                </label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent text-sm">
                  <option value="">All Companies</option>
                  <option value="tech-solutions">Tech Solutions Ltd</option>
                  <option value="digital-services">Digital Services Inc</option>
                </select>
              </div>

              {/* Role Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Role
                </label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent text-sm">
                  <option value="">All Roles</option>
                  <option value="admin">Admin</option>
                  <option value="user">User</option>
                </select>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Status
                </label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent text-sm">
                  <option value="">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
            </div>

            {/* Second row of filters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Date Range Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Date Added Range
                </label>
                <div className="flex space-x-2">
                  <input
                    type="date"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent text-sm"
                    placeholder="Start Date"
                  />
                  <input
                    type="date"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent text-sm"
                    placeholder="End Date"
                  />
                </div>
              </div>

              {/* Search Filter */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Search Users
                </label>
                <input
                  type="text"
                  placeholder="Search by name, email, or company..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent text-sm"
                />
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex justify-end space-x-2">
              <button className="px-4 py-2 border border-gray-300 text-gray-600 rounded-md hover:bg-gray-50 text-sm">
                Reset Filters
              </button>
              <button className="px-4 py-2 bg-[#FF5722] text-white rounded-md hover:bg-[#FF5722]/90 transition-colors text-sm">
                Apply Filters
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date Added</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {[
                  { 
                    name: 'John Doe', 
                    companyName: 'Tech Solutions Ltd',
                    role: 'Admin', 
                    dateAdded: '2024-03-15',
                    status: 'Active' 
                  },
                  { 
                    name: 'Jane Smith', 
                    companyName: 'Digital Services Inc',
                    role: 'User', 
                    dateAdded: '2024-03-20',
                    status: 'Pending' 
                  }
                ].map((user, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap">{user.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{user.companyName}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{user.role}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.dateAdded}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {user.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button className="text-[#FF5722] hover:text-[#FF5722]/90">
                        Manage
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Section 5: Super Administrator Account Details */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center mb-6">
          <ShieldCheckIcon className="h-6 w-6 text-[#FF5722] mr-2" />
          <h2 className="text-lg font-medium">Super Administrator Account Details</h2>
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name
              </label>
              <input
                type="text"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                value="Super Admin"
                readOnly
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                value="admin@example.com"
                readOnly
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Last Login
              </label>
              <input
                type="text"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                value="2024-03-15 14:30:00"
                readOnly
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Account Status
              </label>
              <div className="flex items-center space-x-2">
                <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                  Active
                </span>
                <span className="text-sm text-gray-600">
                  Since: 2024-01-01
                </span>
              </div>
            </div>
          </div>

          <div className="flex space-x-4">
            <button className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#FF5722]/90 transition-colors">
              Change Password
            </button>
            <button className="border border-[#FF5722] text-[#FF5722] px-4 py-2 rounded-md hover:bg-[#FF5722]/10 transition-colors">
              Update Profile
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminSettings; 